/**
 * Client side of zeecore's stored views (zeecore/docs/projections/stored_views.md):
 * a screen's preset periods are served from storage. A response is then
 * either the figures with a `projection` (when they were computed, and
 * `stale_reason` if out of date), or 202 "preparing" while the first copy
 * is built. Shared so every stored-view screen words these the same way.
 */

export type StoredViewStaleReason = 'rules_version' | 'config' | 'too_old';

export interface StoredViewProjection {
  source: 'stored';
  view: string;
  window: string;
  computed_at: string;
  stale_reason?: StoredViewStaleReason;
}

/** Body of a 202 "preparing" answer. */
export interface StoredViewPreparing {
  status: 'preparing';
  retry_after_seconds: number;
}

const STALE_TEXT: Record<StoredViewStaleReason, string> = {
  too_old: 'they have not been refreshed recently',
  config: 'the settings have changed since they were computed',
  rules_version: 'the calculation rules have changed since they were computed',
};

export const STORED_VIEW_PREPARING_MESSAGE =
  'Preparing the figures for this period. This usually takes about a minute, and the page will update on its own.';

/** The "out of date" line for stale stored figures, or null when they are current. */
export function storedViewStaleMessage(projection: StoredViewProjection | null | undefined): string | null {
  if (!projection?.stale_reason) return null;
  return `These figures are out of date: ${STALE_TEXT[projection.stale_reason] ?? 'they are overdue a refresh'}. `
    + 'A refresh has been requested and this page will update when it lands.';
}

/** Seconds to wait before asking again, from a 202 "preparing" response (default 30). */
export async function preparingRetrySeconds(res: Response): Promise<number> {
  try {
    const body = (await res.json()) as Partial<StoredViewPreparing>;
    const s = Number(body.retry_after_seconds);
    return Number.isFinite(s) && s > 0 ? s : 30;
  } catch {
    return 30;
  }
}

/** After stale figures are shown, look again in this many seconds (a refresh takes about a minute). */
export const STORED_VIEW_STALE_RECHECK_SECONDS = 60;

/**
 * For one-off callers (exports) that cannot show "preparing": fetch, and on
 * 202 wait and ask again, up to `maxAttempts`. Null if it never became ready
 * or failed — the caller treats it like any other unavailable section.
 */
export async function fetchStoredViewJson<T>(
  fetcher: () => Promise<Response>,
  maxAttempts = 5,
): Promise<T | null> {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const res = await fetcher();
    if (res.status === 202) {
      if (attempt === maxAttempts) return null;
      const wait = await preparingRetrySeconds(res);
      await new Promise((r) => setTimeout(r, wait * 1000));
      continue;
    }
    if (!res.ok) return null;
    return (await res.json()) as T;
  }
  return null;
}
